/**
 * Centralized Axios Instance
 * Configured with base URL, credentials, and interceptors for token management
 */

import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getStoredAppLocale, LANGUAGE_HEADER } from '../i18n/types';
import { getBrowserTimeZone, TIMEZONE_HEADER } from './timezone';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    skipErrorToast?: boolean;
  }
}

// ============================================
// Axios Instance Configuration
// ============================================

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_SERVER_URL,
  withCredentials: true, // Required for HttpOnly cookies (refresh token)
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_SERVER_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isFormDataBody = (data: unknown): data is FormData => {
  return typeof FormData !== 'undefined' && data instanceof FormData;
};

const removeContentTypeHeader = (config: InternalAxiosRequestConfig) => {
  if (!config.headers) return;

  if (config.headers instanceof AxiosHeaders) {
    config.headers.delete('Content-Type');
    return;
  }

  delete config.headers['Content-Type'];
  delete config.headers['content-type'];
};

// ============================================
// In-Memory Token Storage
// ============================================

let accessToken: string | null = null;
const REFRESH_SESSION_MARKER_COOKIE = 'aibe_refresh_session';
const REFRESH_SESSION_MARKER_STORAGE_KEY = 'refreshSessionPresent';

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = (): string | null => {
  return accessToken;
};

export const clearAccessToken = () => {
  accessToken = null;
};

const canUseBrowserStorage = () => typeof window !== 'undefined';

const hasCookie = (name: string): boolean => {
  if (!canUseBrowserStorage()) {
    return false;
  }

  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith(`${encodeURIComponent(name)}=`));
};

export const markRefreshSessionPresent = () => {
  if (!canUseBrowserStorage()) {
    return;
  }

  window.localStorage.setItem(REFRESH_SESSION_MARKER_STORAGE_KEY, 'true');
};

export const clearRefreshSessionMarker = () => {
  if (!canUseBrowserStorage()) {
    return;
  }

  window.localStorage.removeItem(REFRESH_SESSION_MARKER_STORAGE_KEY);
  document.cookie = `${REFRESH_SESSION_MARKER_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
};

export const hasRefreshSessionMarker = (): boolean => {
  if (!canUseBrowserStorage()) {
    return false;
  }

  return (
    window.localStorage.getItem(REFRESH_SESSION_MARKER_STORAGE_KEY) === 'true' ||
    hasCookie(REFRESH_SESSION_MARKER_COOKIE)
  );
};

let refreshPromise: Promise<string> | null = null;

const AUTH_REFRESH_EXCLUDED_PATHS = new Set([
  '/auth/refresh',
  '/auth/anonymous',
  '/auth/login',
  '/auth/register',
  '/auth/resend-otp',
  '/auth/verify-email',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
]);

const shouldSkipRefreshForRequest = (url?: string) => {
  if (!url) {
    return false;
  }

  const baseUrl =
    import.meta.env.VITE_SERVER_URL ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  const pathname = url.startsWith('http') ? new URL(url).pathname : new URL(url, baseUrl).pathname;

  return AUTH_REFRESH_EXCLUDED_PATHS.has(pathname);
};

const dispatchMediaQuotaRefreshForLimit = (data: unknown) => {
  if (!data || typeof data !== 'object') return;
  const payload = data as { error?: unknown; errorCode?: unknown };
  const code = typeof payload.errorCode === 'string' ? payload.errorCode : payload.error;
  if (typeof code === 'string' && code.endsWith('_LIMIT_REACHED')) {
    window.dispatchEvent(new CustomEvent('media-quota:refresh'));
  }
};

refreshClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.headers) {
    config.headers[LANGUAGE_HEADER] = getStoredAppLocale();
    const timeZone = getBrowserTimeZone();
    if (timeZone) config.headers[TIMEZONE_HEADER] = timeZone;
  }

  return config;
});

export const refreshAccessToken = async (): Promise<string> => {
  if (!hasRefreshSessionMarker()) {
    throw new Error('No refresh session marker');
  }

  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post('/auth/refresh', undefined, {
        skipAuthRefresh: true,
        skipErrorToast: true,
      })
      .then((response) => {
        const newAccessToken = response.data.data?.accessToken;

        if (!newAccessToken) {
          throw new Error('No access token in refresh response');
        }

        setAccessToken(newAccessToken);
        markRefreshSessionPresent();
        return newAccessToken;
      })
      .catch((error) => {
        clearAccessToken();
        clearRefreshSessionMarker();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

// ============================================
// Request Interceptor
// Automatically attach access token to requests
// ============================================

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (isFormDataBody(config.data)) {
      removeContentTypeHeader(config);
    }

    if (config.headers) {
      config.headers[LANGUAGE_HEADER] = getStoredAppLocale();
      const timeZone = getBrowserTimeZone();
      if (timeZone) config.headers[TIMEZONE_HEADER] = timeZone;
    }

    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`[Axios] Request to ${config.url} with token`);
    } else {
      console.log(`[Axios] Request to ${config.url} without token`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// Response Interceptor
// Handle 401 errors and token refresh
// ============================================

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    dispatchMediaQuotaRefreshForLimit(error.response?.data);

    // Handle quota/limit errors (429) - dispatch custom event
    if (error.response?.status === 429) {
      const responseData = error.response.data as {
        error?: string;
        errorCode?: string;
        [key: string]: unknown;
      };
      const errorCode = responseData?.errorCode || responseData?.error;

      // Dispatch custom event based on error type
      if (
        errorCode === 'QUOTA_EXCEEDED' ||
        errorCode === 'TOKEN_QUOTA_EXCEEDED' ||
        errorCode === 'PACKAGE_QUOTA_EXCEEDED' ||
        errorCode === 'USER_TOKEN_LIMIT_EXCEEDED' ||
        errorCode === 'ADVANCE_QUOTA_EXCEEDED' ||
        responseData?.error === 'Quota exceeded' ||
        (typeof responseData?.message === 'string' &&
          responseData.message.toLowerCase().includes('quota'))
      ) {
        window.dispatchEvent(
          new CustomEvent('quota:exceeded', {
            detail: responseData,
          })
        );
      } else if (
        errorCode === 'RATE_LIMIT_EXCEEDED' ||
        responseData?.error === 'Rate limit exceeded'
      ) {
        window.dispatchEvent(
          new CustomEvent('quota:rate-limit', {
            detail: responseData,
          })
        );
      } else if (
        errorCode === 'ANONYMOUS_LIMIT_EXCEEDED' ||
        responseData?.error === 'Anonymous session limit exceeded' ||
        responseData?.error === 'Anonymous message limit exceeded'
      ) {
        window.dispatchEvent(
          new CustomEvent('quota:anonymous-limit', {
            detail: responseData,
          })
        );
      }

      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet
    if (
      error.response?.status === 401 &&
      !originalRequest.skipAuthRefresh &&
      !originalRequest._retry
    ) {
      // Auth bootstrap calls must resolve/reject directly. Retrying refresh from a
      // failed refresh request can leave app initialization waiting forever.
      if (shouldSkipRefreshForRequest(originalRequest.url)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        console.log('[Axios] Attempting token refresh...');

        if (!hasRefreshSessionMarker()) {
          throw new Error('No refresh session marker');
        }

        const newAccessToken = await refreshAccessToken();
        console.log('[Axios] New access token set, retrying request');

        // Retry the original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh failed - user needs to log in again
        console.error('[Axios] Token refresh failed:', refreshError);
        clearAccessToken();
        // Dispatch a custom event to notify AuthContext
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
